import React, { useEffect, useState } from 'react';
import { cx } from '../../ui';
import { BrandedQr } from '../../ui/brandcode';
import { AskScreen, ClaimScreen, Device, HandoverScreen, HomeScreen, PassportScreen, ServiceScreen, TransferScreen } from '../../phone';
import type { Sector } from './sectors';
import { CHAPTERS, chapterIndex, entriesUpTo, fieldsAt, type Fields } from './chapters';

// The maker-branded code lives in shared ui; re-exported so existing imports keep working.
export { BrandedQr } from '../../ui/brandcode';

// ---------- Photos that fall back while the generated unit shots are still being made ----------
export function Img({ src, fallback, ...rest }: { src: string; fallback: string } & Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'>) {
  const [cur, setCur] = useState(src);
  useEffect(() => setCur(src), [src]);
  return <img {...rest} src={cur} onError={() => cur !== fallback && setCur(fallback)} />;
}

// ---------- The maker's rating plate, with their code engraved into it ----------
export function Plate({ s, mini = false, className }: { s: Sector; mini?: boolean; className?: string }) {
  const rows = mini ? s.plate.filter(([k]) => k === 'Model' || k === 'Serial no.' || k === 'Year') : s.plate;
  return (
    <div className={cx('vc-plate', mini && 'is-mini', className)}>
      <i className="vc-rivet" style={{ left: 9, top: 9 }} />
      <i className="vc-rivet" style={{ right: 9, top: 9 }} />
      <i className="vc-rivet" style={{ left: 9, bottom: 9 }} />
      <i className="vc-rivet" style={{ right: 9, bottom: 9 }} />
      <div className="vc-plate-top">
        <span className="vc-plate-maker" style={{ color: s.accent }}>
          {s.maker}
        </span>
      </div>
      <div className="vc-plate-body">
        <div className="vc-plate-rows">
          {rows.map(([k, v]) => (
            <div key={k} className={cx('vc-plate-row', k === 'Serial no.' && 'is-serial')}>
              <span>{k}</span>
              <b className={k === 'Serial no.' ? 'font-mono' : undefined}>{v}</b>
            </div>
          ))}
        </div>
        <div className="vc-plate-code">
          <div className="vc-etch">
            <BrandedQr accent={s.accent} mark={s.mark} seed={s.serial} size={mini ? 56 : 104} />
          </div>
          <div className="vc-plate-meet" style={{ color: s.accent }}>
            Meet your {s.fullModel}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- One scene per chapter: what the person in front of the unit sees ----------
export function Scene({ s, i }: { s: Sector; i: number }) {
  switch (CHAPTERS[i]?.key) {
    case 'built':
      return (
        <div className="vc-paper">
          <div className="vc-erp">
            <div className="vc-erp-top">
              <span>Your ERP</span>
              <span className="vc-erp-ok">Closed</span>
            </div>
            <div className="vc-erp-row">
              <span>Works order</span>
              <b className="font-mono">{s.wo}</b>
            </div>
            <div className="vc-erp-row">
              <span>Part</span>
              <b>{s.model}</b>
            </div>
            <div className="vc-erp-row">
              <span>Serial</span>
              <b className="font-mono">{s.serial}</b>
            </div>
          </div>
          <div className="vc-flow" aria-hidden="true">
            <span />
            <em>plate marked</em>
            <span />
          </div>
          <div className="vc-print">
            <Plate s={s} mini />
          </div>
        </div>
      );
    case 'handover':
      return <StoryPhone s={s}><HandoverScreen s={s} /></StoryPhone>;
    case 'owned':
      return <StoryPhone s={s}><HomeScreen s={s} /></StoryPhone>;
    case 'asked':
      return <StoryPhone s={s}><AskScreen s={s} /></StoryPhone>;
    case 'claim':
      return <StoryPhone s={s}><ClaimScreen s={s} /></StoryPhone>;
    case 'serviced':
      return <StoryPhone s={s}><ServiceScreen s={s} /></StoryPhone>;
    case 'sold':
      return (
        <StoryPhone s={s}>
          <TransferScreen s={s} records={entriesUpTo(s, chapterIndex('sold') - 1).length} />
        </StoryPhone>
      );
    default:
      return <StoryPhone s={s}><PassportScreen s={s} /></StoryPhone>;
  }
}

// The owner phones from src/v2/phone, sized to fit the pinned panel.
function StoryPhone({ s, children }: { s: Sector; children: React.ReactNode }) {
  return (
    <Device s={s} className="vc-sdev">
      {children}
    </Device>
  );
}

// ---------- The record card: what the manufacturer sees, growing chapter by chapter ----------
const FIELD_LABELS: [keyof Fields, (s: Sector) => string][] = [
  ['stage', () => 'Stage'],
  ['owner', () => 'Owner'],
  ['partner', (s) => s.partnerRole.charAt(0).toUpperCase() + s.partnerRole.slice(1)],
  ['warranty', () => 'Warranty'],
];

export function RecordCard({ s, i, compact = false, className }: { s: Sector; i: number; compact?: boolean; className?: string }) {
  const f = fieldsAt(s, i);
  const prev = i > 0 ? fieldsAt(s, i - 1) : null;
  const all = entriesUpTo(s, i);
  const newCount = CHAPTERS[i].entries(s).length;
  const list = [...all].reverse();
  const shown = compact ? list.slice(0, newCount) : list;
  const older = all.length - shown.length;
  return (
    <div className={cx('vc-record', compact && 'is-compact', className)}>
      <div className="vc-rec-head">
        <span className="vc-rec-thumb">
          <Img src={s.product} fallback={s.sceneFallback} alt="" width={100} height={100} />
        </span>
        <div className="min-w-0">
          <div className="vc-rec-name">
            {s.fullModel}
          </div>
          <div className="vc-rec-serial font-mono">{s.serial}</div>
        </div>
        <span className="vc-rec-count">
          {all.length} {all.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>
      <dl className="vc-rec-fields">
        {FIELD_LABELS.map(([k, label]) => {
          const changed = !!prev && prev[k] !== f[k];
          return (
            <div key={k} className={cx('vc-field', changed && 'is-changed')}>
              <dt>{label(s)}</dt>
              <dd key={`${k}-${f[k]}`}>{f[k]}</dd>
            </div>
          );
        })}
      </dl>
      <div className="vc-rec-hist">
        <div className="vc-rec-hist-k">History</div>
        <ol>
          {shown.map((e, k) => (
            <li key={`${e.title}-${all.length - k}`} className={cx(k < newCount && 'is-new')}>
              <span className="vc-e-date font-mono">{e.date}</span>
              <span className="vc-e-title">{e.title}</span>
              <span className="vc-e-meta">{e.meta}</span>
            </li>
          ))}
        </ol>
        {compact && older > 0 && <div className="vc-rec-older">+ {older} earlier {older === 1 ? 'entry' : 'entries'} on the record</div>}
      </div>
    </div>
  );
}
